import { useEffect, useState } from "react";
import {
  AtSign,
  Bell,
  BellOff,
  Check,
  Loader2,
  Mail,
  Volume2,
  VolumeX,
  Vibrate,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useMe, useUpdateMyProfile } from "@/lib/queries";
import { ApiError } from "@/lib/api";
import { usePushSubscription } from "@/lib/push";
import {
  isHapticEnabled,
  isSoundEnabled,
  scoreFeedback,
  setHapticEnabled,
  setSoundEnabled,
} from "@/lib/feedback";

// User-level profile. Email + avatar come from the Google account and
// sync automatically on each sign-in — not exposed here.
export default function Profile() {
  const { data: me, isLoading } = useMe();
  const update = useUpdateMyProfile();

  const [displayName, setDisplayName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (me) {
      setDisplayName(me.displayName);
      setSlug(me.slug);
    }
  }, [me]);

  if (isLoading || !me) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }

  const dirty = displayName !== me.displayName || slug !== me.slug;

  async function save() {
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync({
        displayName: displayName !== me!.displayName ? displayName : undefined,
        slug: slug !== me!.slug ? slug : undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError((e as ApiError).message ?? "Could not save");
    }
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-2xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
          Your profile
        </h1>
        <p className="text-muted-foreground text-sm">
          Display name and @-handle. Email and avatar come from your Google
          account and stay in sync automatically.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Identity</CardTitle>
          <CardDescription>
            Visible in your roster row, on game pages, and on public spectator
            links.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Display name</label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">@-handle</label>
            <div className="flex items-center gap-2">
              <AtSign size={16} className="text-muted-foreground shrink-0" />
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder="hank"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Lowercase letters, digits, hyphens. 2–32 chars. Your profile
              lives at <code className="bg-muted px-1 py-0.5 rounded">cornholio.app/@{slug || "you"}</code>.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-border text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Mail size={14} />
              {me.email ?? "no email on file"}
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex items-center justify-end gap-3 pt-2">
            {saved && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Check size={14} /> Saved
              </span>
            )}
            <Button onClick={save} disabled={!dirty || update.isPending}>
              {update.isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Saving…
                </>
              ) : (
                "Save changes"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <FeedbackCard />
      <PushCard />
    </div>
  );
}

function FeedbackCard() {
  const [sound, setSound] = useState(() => isSoundEnabled());
  const [haptic, setHaptic] = useState(() => isHapticEnabled());
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Volume2 size={18} /> Scoring feedback
        </CardTitle>
        <CardDescription>
          Sound click and phone vibration on every score tap. On by default.
          The scoring UX is much snappier with them on.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Toggle
          icon={sound ? <Volume2 size={16} /> : <VolumeX size={16} />}
          label="Sound on score"
          on={sound}
          onChange={(v) => {
            setSound(v);
            setSoundEnabled(v);
            if (v) scoreFeedback();
          }}
        />
        <Toggle
          icon={<Vibrate size={16} />}
          label="Haptic on score"
          on={haptic}
          onChange={(v) => {
            setHaptic(v);
            setHapticEnabled(v);
            if (v) scoreFeedback();
          }}
        />
      </CardContent>
    </Card>
  );
}

function Toggle({
  icon,
  label,
  on,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  on: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className="w-full flex items-center justify-between p-3 rounded-md border border-border hover:bg-muted/50 text-left"
    >
      <span className="flex items-center gap-2 text-sm">
        {icon} {label}
      </span>
      <span
        className={`inline-flex items-center text-xs font-display uppercase tracking-widest ${
          on ? "text-primary" : "text-muted-foreground"
        }`}
      >
        {on ? "On" : "Off"}
      </span>
    </button>
  );
}

function PushCard() {
  const push = usePushSubscription();
  const [testStatus, setTestStatus] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell size={18} /> Notifications
        </CardTitle>
        <CardDescription>
          Get a ping when a game you're in finishes. Works on phone home-screen
          installs and modern desktop browsers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!push.supported && (
          <p className="text-sm text-muted-foreground">
            This browser doesn't support web push. Install the app from your
            phone's share menu to enable.
          </p>
        )}

        {push.supported && (
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm">
              <div className="font-medium inline-flex items-center gap-2">
                {push.subscribed ? (
                  <>
                    <Bell size={14} className="text-primary" /> Enabled
                  </>
                ) : (
                  <>
                    <BellOff size={14} className="text-muted-foreground" /> Off
                  </>
                )}
              </div>
              {push.permission === "denied" && (
                <p className="text-xs text-destructive mt-0.5">
                  Permission denied — re-enable in browser settings.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {push.subscribed && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={push.busy}
                  onClick={async () => {
                    setTestStatus("Sending…");
                    const out = await push.test();
                    if (out) {
                      setTestStatus(
                        `Sent ${out.sent}, pruned ${out.pruned}, failed ${out.failed}`,
                      );
                    } else {
                      setTestStatus("No test sent");
                    }
                    setTimeout(() => setTestStatus(null), 3500);
                  }}
                >
                  Send test
                </Button>
              )}
              {push.subscribed ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={push.busy}
                  onClick={() => push.disable()}
                >
                  {push.busy ? <Loader2 size={14} className="animate-spin" /> : "Disable"}
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={push.busy || push.permission === "denied"}
                  onClick={() => push.enable()}
                >
                  {push.busy ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    "Enable"
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {testStatus && (
          <p className="text-xs text-muted-foreground">{testStatus}</p>
        )}
        {push.error && <p className="text-sm text-destructive">{push.error}</p>}
      </CardContent>
    </Card>
  );
}
